import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { CreateEntityButton } from 'record-it-components/misc/CreateEntityButton/CreateEntityButton';
import { MessageBox, MessageBoxType } from 'record-it-components/misc/MessageBox/MessageBox';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { UserGroupUtils } from '../../../classes/entities/UserGroup/UserGroupUtils';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useStartCreatingWorkTimeEntryWithoutStopwatch } from '../../../hooks/createWorkTimeEntry/useStartCreatingWorkTimeEntryWithoutStopwatch/useStartCreatingWorkTimeEntryWithoutStopwatch';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useStopwatchInfos } from '../../../hooks/stopwatch/useStopwatchInfos';
import { StartNewStopwatchFixedButton } from '../../timeTracking/StartNewStopwatchFixedButton/StartNewStopwatchFixedButton';
import { StopwatchList } from '../../timeTracking/StopwatchList/StopwatchList';
import { TodaysWorkTimeInfo } from '../../workTime/TodaysWorkTimeInfo/TodaysWorkTimeInfo';
import styles from './HomePage.module.css';
export type HomePageProps = Record<string, never>;
export const HomePage: FunctionComponent<HomePageProps> = props => {
  const $ = _c(25);
  prepareProps({
    props,
    ignoreChildren: true
  });
  useConfigureBreadcrumbsWithBuilder(_temp);
  const {
    t
  } = useTranslation();
  const infos = useStopwatchInfos();
  const userGroups = useEntities(EntityName.UserGroup, "getAll");
  const userGroupsAvailable = userGroups.length > 0;
  const {
    startCreatingWorkTimeEntryWithoutStopwatch
  } = useStartCreatingWorkTimeEntryWithoutStopwatch();
  let t0;
  if ($[0] !== startCreatingWorkTimeEntryWithoutStopwatch) {
    t0 = userGroup => {
      assertNotNullOrUndefined(userGroup, "no userGroup got selected, did you forget to hide the button?");
      startCreatingWorkTimeEntryWithoutStopwatch({
        userGroupId: userGroup.id
      });
    };
    $[0] = startCreatingWorkTimeEntryWithoutStopwatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleCreateEntityClicked = useEventCallback(t0);
  let t1;
  if ($[2] !== handleCreateEntityClicked || $[3] !== t || $[4] !== userGroups || $[5] !== userGroupsAvailable) {
    t1 = () => {
      const elements = [];
      if (userGroupsAvailable) {
        elements.push(<CreateEntityButton key="HomePage.CreateWorkTimeEntryButton" text={t("components.page.HomePage.createWorkTimeEntry")} dropdownItems={userGroups} getDropdownItemDisplayText={UserGroupUtils.displayTextGetter} getDropdownItemKey={UserGroupUtils.keyGetter} onCreateEntity={handleCreateEntityClicked} data-testid="HomePage.CreateWorkTimeEntryButton" />);
      }
      return elements;
    };
    $[2] = handleCreateEntityClicked;
    $[3] = t;
    $[4] = userGroups;
    $[5] = userGroupsAvailable;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const renderRightContentElements = t1;
  let t2;
  if ($[7] !== t) {
    t2 = t("components.page.HomePage.title");
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== renderRightContentElements || $[10] !== t2) {
    t3 = <PageContentHeader title={t2} iconConfig={iconConfigurations.prefixed.page.HomePage.titleIcon} renderRightContentElements={renderRightContentElements} />;
    $[9] = renderRightContentElements;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t || $[13] !== userGroupsAvailable) {
    t4 = !userGroupsAvailable ? <MessageBox type={MessageBoxType.Warning} data-testid="HomePage.WarningMessageBox">{t("components.page.HomePage.noUserGroupsAvailable")}</MessageBox> : null;
    $[12] = t;
    $[13] = userGroupsAvailable;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TodaysWorkTimeInfo className={styles.todaysWorkTimeInfo} />;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== infos) {
    t6 = <StopwatchList stopwatchInfos={infos} />;
    $[16] = infos;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== userGroupsAvailable) {
    t7 = userGroupsAvailable ? <StartNewStopwatchFixedButton data-testid="HomePage.StartNewStopwatchFixedButton" /> : null;
    $[18] = userGroupsAvailable;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t6 || $[23] !== t7) {
    t8 = <PageContentContainer>{t3}{t4}{t5}{t6}{t7}</PageContentContainer>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
function _temp() {}